import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Every number a person reads.
 *
 * Tabular figures are applied here rather than by remembering a class at each
 * call site: the failure mode is one ragged column in a table of otherwise
 * aligned money, and nobody notices it in review. The `data-numeric` attribute
 * is what the stylesheet keys off, and what the render check counts.
 *
 * It takes a string, not a number, because the string has already been through
 * the one formatter. Passing a raw number here would mean formatting happened
 * somewhere else.
 */
export function Num({
  children,
  align = 'start',
  className,
  ...rest
}: {
  children: ReactNode;
  align?: 'start' | 'end';
  className?: string;
  /**
   * Anything else, forwarded.
   *
   * The first version took exactly three props and silently dropped the rest,
   * so a `data-` hook put on a Num vanished - and the test that looked for it
   * found nothing while the number was on screen the whole time. A component
   * that accepts a prop and ignores it is worse than one that refuses it.
   */
} & Omit<HTMLAttributes<HTMLSpanElement>, 'className' | 'children'>): JSX.Element {
  return (
    <span
      {...rest}
      data-numeric
      className={cx(align === 'end' && 'text-right tabular-nums', className)}
    >
      {children}
    </span>
  );
}
